// Workspace context — Pacote 14.
// Mantém o workspace ATIVO (current household) e a lista de todos os
// workspaces do user. Estado vive no React + persiste em localStorage.
//
// Convenção: activeId = "all" = sem filtro (mostra dados pessoais + de todos
// os households). Qualquer outro valor = id do household selecionado.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { apiFetch } from "@/lib/api"

export interface Workspace {
  id: string
  name: string
  kind: "personal" | "business"
  cnpj: string | null
  colorTheme: string | null
  icon: string | null
  role: "owner" | "member" | "viewer"
  joinedAt: string
  createdAt: string
  createdBy: string
  memberCount: number
}

const ALL_KEY = "all"
const STORAGE_KEY = "novavida.activeWorkspace"

interface WorkspaceContextValue {
  workspaces: Workspace[]
  activeId: string                         // "all" ou householdId
  active: Workspace | null                  // null se activeId="all"
  setActive: (id: string) => void
  reload: () => Promise<void>
  loading: boolean
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [activeId, setActiveIdState] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY) ?? ALL_KEY
  })
  const [loading, setLoading] = useState(true)

  async function reload() {
    setLoading(true)
    try {
      const r = await apiFetch<{ households: Workspace[] }>("/households")
      setWorkspaces(r.households)
      // Se o activeId atual não existe mais (workspace deletada), volta pra "all"
      if (activeId !== ALL_KEY && !r.households.some((w) => w.id === activeId)) {
        setActive(ALL_KEY)
      }
    } catch {
      setWorkspaces([])
    } finally {
      setLoading(false)
    }
  }

  function setActive(id: string) {
    setActiveIdState(id)
    if (id === ALL_KEY) localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, id)
  }

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const active = activeId === ALL_KEY ? null : workspaces.find((w) => w.id === activeId) ?? null

  return (
    <WorkspaceContext.Provider value={{ workspaces, activeId, active, setActive, reload, loading }}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext)
  if (!ctx) throw new Error("useWorkspace must be used within WorkspaceProvider")
  return ctx
}

export const WORKSPACE_ALL = ALL_KEY
